import { Component, OnInit, OnDestroy } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { WebService, lastValueFrom, InventoryDataService, PromptService, RmsMessageService, RmsPulldownService, NavTabService } from '@services';
import { CreateOfferSampleComponent } from './create-offer-sample/create-offer-sample.component';
import { interval, Subscription } from 'rxjs';
import { PurchaseOrdersComponent } from '../../inventory/purchase-orders/purchase-orders.component';


@Component({
  selector: 'app-offer-samples',
  imports: [RmsModule],
  templateUrl: './offer-samples.component.html',
  styleUrl: './offer-samples.component.scss'
})
export class OfferSamplesComponent implements OnInit, OnDestroy {
  offerSamples : any[] = []
  samples: any[] = []
  completedSamples: any[] = []
  suppliers: any[] = []
  products: any[] = [];
  ShowCompletedSamples: boolean = false;
  ShowRejectedArrivals = false;
  saving = false;
  private refreshing = false;
  SampleSwitcher: string = ''
  private refreshSub!: Subscription;
  offerSampleColumns: Column[] = [
    { label: 'ID', key: 'InventoryID', width: 20 },
    { label: 'Name', key: 'InventoryTypeName', width: 30 },
    {label: 'Preship', type: 'checkbox', key: 'PreShip', SearchType: 'checkbox'},
    {label: 'Offer Sample', type: 'checkbox', key: 'IsOfferSample', SearchType: 'checkbox'},
  ]
  completedSampleColumns: Column[] = [
    { label: 'Session', key: 'CuppingSessionKey', width: 9 },
    { label: 'Ref #', key: 'InventoryID', width: 9 },
    { label: 'Name', key: 'InventoryTypeName', width: 30 },
    { label: 'Preship', type: 'checkbox', key: 'PreShip', SearchType: 'checkbox' },
    { label: 'Offer Sample', type: 'checkbox', key: 'IsOfferSample', SearchType: 'checkbox' },
    { label: 'Result', key: 'SampleStatus' },
    { label: 'Contract terms', key: 'SampleApprovalTerm' },
  ]
  SelectedOfferSample: any | null = null;
  Buttons: HeaderButton[] = [
    {
      label: "New Offer Sample", class: 'btn-bg-green', onAction: () => this.OpenOfferSample()
    },
    {label: this.SampleSwitcher, class: 'btn-bg-blue', onAction: () => this.ToggleCompletedSamples()}
  ]

  constructor(private web: WebService, private inventoryData: InventoryDataService, private prompt: PromptService, private message: RmsMessageService, private pulldown: RmsPulldownService, private tab: NavTabService) { }

  async ngOnInit() {
    this.refreshSub?.unsubscribe();
    await this.loadData();

    this.refreshSub = interval(10000).subscribe(() => {
      this.refreshSamples();
    });

    this.SampleSwitcher =
      this.ShowCompletedSamples
        ? 'View Uncupped Samples'
        : 'View Cupped Samples';

    this.Buttons[1].label = this.SampleSwitcher;

  }

  ngOnDestroy() {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
    }
  }


  async loadData() {
      this.samples = this.sortSamples(
          await lastValueFrom(this.web.getWebCall('cupping', 'get_sample_queue'))
      );
      this.completedSamples = this.sortSamples(
        await lastValueFrom(this.web.getWebCall('inventory', 'coffee/completed_samples'))
      );
    const data = await this.inventoryData.getData({ keys: ['Suppliers', 'Products'] });
    this.suppliers = data['Suppliers'];
    this.products = data['Products'];
  }

  ToggleCompletedSamples() {
    this.ShowCompletedSamples = !this.ShowCompletedSamples;

    this.SampleSwitcher =
      this.ShowCompletedSamples
        ? 'View Uncupped Samples'
        : 'View Cupped Samples';

    this.Buttons[1].label = this.SampleSwitcher;
  }

  // separate just the samples reload so you’re not reloading everything
  async refreshSamples() {
    if (this.refreshing || this.SelectedOfferSample || this.saving) return;
    this.refreshing = true;
    try {
      const [queue, completed] = await Promise.all([
        lastValueFrom(this.web.getWebCall('cupping', 'get_sample_queue', { skipLoader: true })),
        lastValueFrom(this.web.getWebCall('inventory', 'coffee/completed_samples', { skipLoader: true }))
      ]);
      this.samples = this.sortSamples(queue);
      this.completedSamples = this.sortSamples(completed);
    } catch { /* Retain the last successful view while offline. */ }
    finally { this.refreshing = false; }
  }
  setSampleView(view: string) {
    this.ShowCompletedSamples = view !== 'queue';
    this.ShowRejectedArrivals = view === 'rejected';
  }
  get rejectedArrivals(): any[] {
    const latest = new Map<number, any>();
    for (const row of this.completedSamples) {
      if (row.IsOfferSample || row.PreShip) continue;
      const previous = latest.get(row.InventoryID);
      if (!previous || Number(row.CuppingSessionDetailID) > Number(previous.CuppingSessionDetailID)) latest.set(row.InventoryID, row);
    }
    return [...latest.values()].filter(row => Number(row.SASTermID) === 7 && row.WasApproved === false && !row.Recup);
  }
  rejectionNextStep(sample: any): string {
    const term = Number(sample.SASTermID);
    if (term === 1 || term === 7) return 'Replacement required. Notify the supplier with the rejection reason.' + (term === 7 ? ' Coordinate the arrival with the trader and Inbound.' : ' Request a replacement preship sample.');
    if (term === 2 || term === 8) return 'No Approval No Sale: supplier notification and cancellation review required.' + (sample.Hedged ? ' This reference is hedged; resolve the hedge before cancellation.' : '');
    return 'Review the contract terms with the trader before deciding the next step.';
  }
  async selectSample(event: any) {
    const sample = event.row;
    const params = [{ name: 'InventoryID', value: sample.InventoryID }, {name: 'IsOfferSample', value: sample.IsOfferSample}, {name: 'PreShip', value: sample.PreShip}];
    // Preparation rows may not have a detail yet; completed results select an exact historical detail.
    if (this.ShowCompletedSamples && Number(sample.CuppingSessionDetailID) > 0) {
      params.push({name: 'CuppingSessionDetailID', value: sample.CuppingSessionDetailID});
    }
    try {
      const result = await lastValueFrom(this.web.getWebCall('cupping', 'get_sample_details', { params }));
      this.SelectedOfferSample = result?.[0] ? { ...sample, ...result[0] } : null;
      if (!this.SelectedOfferSample) this.message.error('Sample details were not found. Please refresh the sample list.');
    } catch {
      this.message.error('Unable to load sample details. Please try again.');
    }
  }

  OpenOfferSample(offerSample?: any) {
    if (offerSample) {
      const data = { ...offerSample }
    }
    const closed = this.pulldown.open(CreateOfferSampleComponent, {
      top: '0',
      width: 'min(720px, 96vw)',
      title: offerSample ? 'Offer sample details' : 'New offer sample',
      data: offerSample ? { ...offerSample } : null
    })

    closed.afterClosed().subscribe(async (value) => {
      if (!value) return;

      if (value.cancelled) return;

      if (value.createPurchaseOrder) {
        this.createPOFromOffer(value);
        return;
      }

      if (value.ManuallyEnterName) {
        value.InventoryTypeID = null;
      }
      else {
        value.InventoryTypeName = null
      }
      if (value.ManuallyEnterSupplier) {
        value.SupplierID = null;
      }
      else {
        value.SupplierName = null
      }

      const res = await lastValueFrom(this.web.getWebCall('cupping', 'merge_offering_sample', { body: value }))
      await this.loadData();

      if (offerSample) {
        const params = [{ name: 'InventoryID', value: res[0].OfferingSampleID }, { name: 'IsOfferSample', value: true }]
        const result = await lastValueFrom(this.web.getWebCall('cupping', 'get_sample_details', { params: params }));
        this.SelectedOfferSample = result[0]
        if (this.ShowCompletedSamples) {
          this.SelectedOfferSample = { ...value, ...this.SelectedOfferSample, }
        }
      }
      this.message.success('Offer sample created successfully');
    })
  }
  onSaveOfferSample() {
    const { closed$ } = this.prompt.open({
      promptText: 'Are you sure you want to save this offer sample?',
      buttons: [{ label: 'Save', buttonClass: 'btn-bg-green', value: true }]
    })
    closed$.subscribe(async (value) => {
      if (!value) return;

      const payload = { ...this.SelectedOfferSample };
      if (payload.IsOfferSample) { payload.OfferSampleID = payload.InventoryID; payload.InventoryID = null; }

      const res = await lastValueFrom(this.web.getWebCall('cupping', 'merge_offering_sample', { body: payload }))
      await this.loadData();
      if (this.SelectedOfferSample.DateRoasted) {
        this.BackToList();
      }
      else {
        const params = [{ name: 'InventoryID', value: res[0].OfferingSampleID }, { name: 'IsOfferSample', value: true }]
        const result = await lastValueFrom(this.web.getWebCall('cupping', 'get_sample_details', { params: params }));

        this.SelectedOfferSample = result[0]
      }
      this.message.success('Sample updated successfully');
    })
  }
  async onSaveSample() {
    if(this.saving || !this.SelectedOfferSample || this.ShowCompletedSamples) return;
    this.saving = true;
    const payload = { ...this.SelectedOfferSample };
    if(payload.IsOfferSample) {payload.OfferSampleID=payload.InventoryID;payload.InventoryID=null;}
    try {
      await lastValueFrom(this.web.getWebCall('cupping','merge_cupping_sample_details',{body:payload}));
      await this.loadData();
      this.BackToList();
      this.message.success(payload.DateRoasted ? 'Sample saved and ready for cupping' : 'Sample preparation saved');
    } catch {this.message.error('Unable to save the sample. Your changes are still available.');}
    finally {this.saving=false;}
  }
  BackToList() {
    this.SelectedOfferSample = null;
  }

  getSampleTypeLabel(sample: any): string {
    if (sample.SASTermID === 1 || sample.SASTermID === 2) return 'Preship Sample';
    if (sample.SASTermID === 7 || sample.SASTermID === 8) return 'Arrival Sample';
    return sample.PreShip ? 'Preship Sample' : 'Arrival Sample';
  }

  getSasTermStyle(sample: any) {
    const isNans = sample.SASTermID === 2 || sample.SASTermID === 8;
    return isNans
      ? { text: '#dc2626' }
      : { text: '#2563eb' };
  }

  onSendRejectionEmail(sample: any) {
    const recipients = Number(sample.SASTermID) === 8 ? 'Supplier, responsible trader, Inbound / Traffic and Accounting' : Number(sample.SASTermID) === 7 ? 'Supplier, responsible trader and Inbound / Traffic' : 'Supplier';
    this.message.info(`Email simulated to ${recipients}. No email sent. ${this.rejectionNextStep(sample)}`);
  }

  sortSamples(data: any[]) {
    return data
      .map(row => ({
        ...row,
        SampleStatus: row.Recup ? 'Recup requested' : row.WasApproved === true ? 'Approved' : row.WasApproved === false ? 'Rejected' : 'Pending',

        // ✅ Add watermark condition
        rowHighlight: row.Recupping
          ? {
            text: 'RECUP',
            justify: 'center',
            margin: '0',
            scope: 'row',
            // optional styling (tweak as needed)
            background: 'rgba(255, 80, 80, 0.06)',
            textColor: 'rgba(180, 0, 0, 0.3)'
          }
          : null,

        // (optional) also add row styling for quick scan
        rowClass: {
          'recup-row': row.Recupping === true
        }
      }))
      .sort((a, b) => b.InventoryID - a.InventoryID);
  }
  createPOFromOffer(offerSample: any) {
    const { closed$ } = this.prompt.open({
      promptText: 'Are you sure you want to create a PO from this offer sample?',
      buttons: [{ label: 'Create PO', buttonClass: 'btn-bg-green', value: true }]
    })

    closed$.subscribe(async (value) => {
      if (!value) return;

      const navItem = this.tab.getNavItemFromTitle("Purchase Orders");
      if (navItem) {
        this.tab.addTab({ title: navItem.title, icon: navItem.icon, componentKey: navItem?.componentKey, data: { OfferSample: offerSample, NewPurchaseOrder: true } })
      }
    })
  }
}
